import designSystemStyles from '@shared/styles/designSystemStyles';
import { SmallText, Text } from '@shared/designSystem/Texts';
import { CSSProperties } from 'react';
// Main vertical container for pages

interface BaseContainerProps {
    children: React.ReactNode
    maxWidth?: string
    maxHeight?: string
    minHeight?: string
    height?: CSSProperties["height"]
    width?: string
    margin?: CSSProperties["margin"]
    gap?: string
    backgroundColor?: string
    padding?: string
    paddingLeft?: string
    paddingTop?: string
    paddingRight?: string
    paddingBottom?: string
    alignment?: CSSProperties["alignItems"]
    justifyContent?: CSSProperties["justifyContent"]
    minWidth?: CSSProperties["minWidth"]
    overflow?: CSSProperties["overflow"]
    borderRadius?: CSSProperties["borderRadius"]
    title?: string
    className?: string
    name?: string
}

interface VerticalContainerProps extends BaseContainerProps {
    scrollable?: boolean
}

interface HorizontalContainerProps extends BaseContainerProps {
    horizontalPadding?: string
    justifyContent?: CSSProperties["justifyContent"]
    gridTemplateColumns?: CSSProperties["gridTemplateColumns"]
    flexWrap?: CSSProperties["flexWrap"]
    flexShrink?: CSSProperties["flexShrink"]
}

interface ColumnsContainerProps extends BaseContainerProps {
    columns?: number
    minColumnWidth?: string
}

export const VerticalMainContainer: React.FC<VerticalContainerProps> = ({ 
    children, 
    maxWidth = '1200px',
    gap = designSystemStyles.gapLg,
    backgroundColor = designSystemStyles.colorBackgroundGray3,
    className,
    name
}) => {
    return (
        <div className={['DSVerticalMainContainer', className].filter(Boolean).join(' ')} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: gap,
            maxWidth: maxWidth,
            margin: '0 auto',
            padding: designSystemStyles.paddingLgScreen,
            fontFamily: designSystemStyles.fontFamily,
            backgroundColor: backgroundColor
        }}>
            {children}
        </div>
    );
};

export const VerticalContainer: React.FC<VerticalContainerProps> = ({ 
    children, 
    maxWidth = '100%',
    maxHeight = undefined,
    minHeight = undefined,
    height = undefined,
    width = "100%",
    margin = '0 auto',
    gap = designSystemStyles.gapMd,
    backgroundColor = 'transparent',
    alignment = 'start',
    justifyContent = undefined,
    padding = designSystemStyles.paddingZero,
    paddingLeft,
    paddingTop,
    paddingRight,
    paddingBottom,
    scrollable = false,
    minWidth = undefined,
    overflow = undefined,
    borderRadius = undefined,
    className,
    name = null
}) => {

    if (padding !== designSystemStyles.paddingZero) {
        paddingLeft = padding
        paddingRight = padding
        paddingTop = padding
        paddingBottom = padding

        // this way, no weird order bugs occur (conflicting/overwriting paddings)
    }

    return (
        <div className={[ (name != null ? `DSName:${name}` : ''), 'DSVerticalContainer', className].filter(Boolean).join(' ')} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: gap,
            width: width,
            maxWidth: maxWidth,
            maxHeight: maxHeight,
            minHeight: minHeight,
            minWidth: minWidth,
            height: height,
            margin: margin,
            padding: padding,
            paddingLeft: paddingLeft ?? padding,
            paddingRight: paddingRight ?? padding,
            paddingTop: paddingTop ?? padding,
            paddingBottom: paddingBottom ?? padding,
            fontFamily: designSystemStyles.fontFamily,
            backgroundColor: backgroundColor,
            borderRadius,
            alignItems: alignment,
            justifyContent: justifyContent,
            overflow: overflow,
            overflowY: scrollable ? 'auto' : undefined,
            flexGrow: scrollable ? '1' : '0',
            overflowX: scrollable ? 'hidden' : undefined
        }}>
            {children}
        </div>
    );
};

export const HorizontalSpacer: React.FC = () => {
    return (
        <div className="DSHorizontalSpacer" style={{width: '100%'}}/>
    )
}

export const VerticalSpacer: React.FC = () => {
    return (
        <div className="DSVerticalSpacer" style={{height: '100%'}}/>
    )
}

export const HorizontalContainer: React.FC<HorizontalContainerProps> = ({ 
    children, 
    maxHeight = undefined,
    maxWidth = "100%",
    height = undefined,
    width = "100%",
    horizontalPadding = designSystemStyles.paddingZero,
    padding = designSystemStyles.paddingZero,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom,
    backgroundColor = 'transparent',
    alignment = 'center',
    justifyContent = designSystemStyles.justifyContentLeft,
    gap = designSystemStyles.gapMd,
    margin = '0',
    minWidth = undefined,
    overflow = undefined,
    borderRadius = undefined,
    gridTemplateColumns = undefined,
    flexWrap = undefined,
    flexShrink = undefined,
    title = undefined,
    className,
    name
}) => {

    if (padding != designSystemStyles.paddingZero) {
        paddingLeft = padding
        paddingRight = padding
        paddingTop = padding
        paddingBottom = padding
        // this way, no weird order bugs occur (conflicting/overwriting paddings)
    }

    return (
        <div
            className={[(name != null ? `DSName:${name}` : ''), 'DSHorizontalContainer', className].filter(Boolean).join(' ')}
            title={title}
            style={{
                display: gridTemplateColumns ? 'grid' : 'flex',
                flexDirection: 'row',
                flexWrap: flexWrap,
                flexShrink: flexShrink,
                gridTemplateColumns: gridTemplateColumns,
                gap: gap,
                height: height,
                maxHeight: maxHeight,
                width: width,
                maxWidth: maxWidth,
                minWidth: minWidth,
                margin: margin,            
                padding: padding,
                paddingLeft: paddingLeft ?? horizontalPadding ?? padding,
                paddingRight: paddingRight ?? horizontalPadding ?? padding,
                paddingTop: paddingTop ?? padding,
                paddingBottom: paddingBottom ?? padding,
                fontFamily: designSystemStyles.fontFamily,
                backgroundColor: backgroundColor,
                borderRadius,
                alignItems: alignment,
                justifyContent: justifyContent,
                overflow: overflow
            }}
        >
            {children}
        </div>
    );
};

export const ColumnsContainer: React.FC<ColumnsContainerProps> = ({
    children,
    maxWidth = "100%",
    maxHeight = undefined,
    height = undefined,
    width = "100%",
    margin = "0",
    gap = designSystemStyles.gapMd,
    backgroundColor = "transparent",
    padding = designSystemStyles.paddingZero,
    paddingLeft,
    paddingTop,
    paddingRight,
    paddingBottom,
    minWidth = undefined,
    overflow = undefined,
    borderRadius = undefined,
    title = undefined,
    className,
    name,
    columns = undefined,
    minColumnWidth = undefined
}) => {
    if (padding !== designSystemStyles.paddingZero) {
        paddingLeft = padding
        paddingRight = padding
        paddingTop = padding
        paddingBottom = padding
    }

    const gridTemplateColumns = minColumnWidth
        ? `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`
        : `repeat(${columns ?? 1}, minmax(0, 1fr))`

    return (
        <div
            className={[(name != null ? `DSName:${name}` : ''), 'DSColumnsContainer', className].filter(Boolean).join(' ')}
            title={title}
            style={{
                display: 'grid',
                gridTemplateColumns,
                gap,
                width,
                maxWidth,
                maxHeight,
                height,
                minWidth,
                margin,
                padding,
                paddingLeft: paddingLeft ?? padding,
                paddingRight: paddingRight ?? padding,
                paddingTop: paddingTop ?? padding,
                paddingBottom: paddingBottom ?? padding,
                fontFamily: designSystemStyles.fontFamily,
                backgroundColor,
                overflow,
                borderRadius
            }}
        >
            {children}
        </div>
    )
}

interface CardContainerProps {
    children: React.ReactNode
    maxWidth?: string
    width?: string
    padding?: string
    backgroundColor?: string
    borderRadius?: string
    boxShadow?: string
    className?: string
}

interface SimpleCardContainerProps {
    children: React.ReactNode
    width?: CSSProperties["width"]
    height?: CSSProperties["height"]
    padding?: CSSProperties["padding"]
    margin?: CSSProperties["margin"]
    backgroundColor?: string
    border?: CSSProperties["border"]
    borderRadius?: string
    boxShadow?: string
    variant?: "default" | "inlineSetup" | "destructiveInlineSetup"
    className?: string
}

export const CardContainer: React.FC<CardContainerProps> = ({ 
    children, 
    maxWidth = '100%',
    width = '100%',
    padding = designSystemStyles.paddingSm,
    backgroundColor = designSystemStyles.colorBackgroundWhite,
    borderRadius = designSystemStyles.cornerRadiusComponent,
    boxShadow = designSystemStyles.shadowCard,
    className
}) => {
    return (
        <div className={['DSCardContainer', className].filter(Boolean).join(' ')} style={{
            display: 'flex',
            flexDirection: 'column',
            width: width,
            maxWidth: maxWidth,
            padding: padding,
            backgroundColor: backgroundColor,
            borderRadius: borderRadius,
            boxShadow: boxShadow,
            fontFamily: designSystemStyles.fontFamily
        }}>
            {children}
        </div>
    )
}

export const SimpleCardContainer: React.FC<SimpleCardContainerProps> = ({
    children,
    width = '100%',
    height = 'auto',
    padding = designSystemStyles.paddingSm,
    margin,
    backgroundColor = designSystemStyles.colorBackgroundWhite,
    border,
    borderRadius = designSystemStyles.cornerRadiusComponent,
    boxShadow = designSystemStyles.shadowCard,
    variant = "default",
    className,
}) => {
    const isInlineSetup = variant === "inlineSetup" || variant === "destructiveInlineSetup"
    const resolvedPadding = isInlineSetup ? designSystemStyles.paddingMd : padding
    const resolvedBackgroundColor = isInlineSetup ? designSystemStyles.colorBackgroundGray3 : backgroundColor
    const resolvedBorderRadius = isInlineSetup ? designSystemStyles.cornerRadiusComponent : borderRadius
    const resolvedBoxShadow = isInlineSetup ? "none" : boxShadow
    const resolvedBorder = variant === "destructiveInlineSetup"
        ? `1px solid ${designSystemStyles.colorError}`
        : border

    return (
        <div
            className={['DSSimpleCardContainer', className].filter(Boolean).join(' ')}
            style={{
                width,
                height,
                padding: resolvedPadding,
                margin,
                backgroundColor: resolvedBackgroundColor,
                borderRadius: resolvedBorderRadius,
                border: resolvedBorder,
                boxSizing: "border-box",
                boxShadow: resolvedBoxShadow,
                fontFamily: designSystemStyles.fontFamily,
            }}
        >
            {children}
        </div>
    );
}

export const TableHeaderContainer: React.FC<HorizontalContainerProps> = ({ 
    children, 
    maxHeight = undefined,
    maxWidth = "100%",
    height = undefined,
    width = "100%",
    horizontalPadding = designSystemStyles.paddingSm,
    padding = designSystemStyles.paddingSm,
    paddingLeft = horizontalPadding,
    paddingRight = horizontalPadding,
    paddingTop = designSystemStyles.paddingSm,
    paddingBottom = designSystemStyles.paddingSm,
    backgroundColor = designSystemStyles.colorBackgroundGray3,
    alignment = 'center',
    justifyContent = designSystemStyles.justifyContentLeft,
    gap = designSystemStyles.gapMd,
    gridTemplateColumns = undefined,
    className
}) => {
    return (
        <div className={['DSTableHeaderContainer', className].filter(Boolean).join(' ')} style={{
            display: gridTemplateColumns ? 'grid' : 'flex',
            flexDirection: 'row',
            gridTemplateColumns: gridTemplateColumns,
            gap: gap,
            height: height,
            maxHeight: maxHeight,
            width: width,
            maxWidth: maxWidth,
            margin: '0',            
            padding: padding,
            paddingLeft: paddingLeft,
            paddingRight: paddingRight,
            paddingTop: paddingTop,
            paddingBottom: paddingBottom,
            fontFamily: designSystemStyles.fontFamily,
            backgroundColor: backgroundColor,
            alignItems: alignment,
            justifyContent: justifyContent,
            borderRadius: designSystemStyles.cornerRadiusComponent
        }}>
            {children}
        </div>
    );
};

export const TableRowContainer: React.FC<HorizontalContainerProps> = ({
    children,
    maxHeight = undefined,
    maxWidth = "100%",
    height = undefined,
    minHeight = undefined,
    width = "100%",
    horizontalPadding = designSystemStyles.paddingSm,
    padding = designSystemStyles.paddingSm,
    paddingLeft = horizontalPadding,
    paddingRight = horizontalPadding,
    paddingTop = designSystemStyles.paddingSm,
    paddingBottom = designSystemStyles.paddingSm,
    backgroundColor = designSystemStyles.colorBackgroundWhite,
    alignment = 'center',
    justifyContent = designSystemStyles.justifyContentLeft,
    gap = designSystemStyles.gapMd,
    gridTemplateColumns = undefined,
    className
}) => {
    return (
        <div className={['DSTableRowContainer', className].filter(Boolean).join(' ')} style={{
            display: gridTemplateColumns ? 'grid' : 'flex',
            flexDirection: 'row',
            gridTemplateColumns: gridTemplateColumns,
            gap: gap,
            height: height,
            maxHeight: maxHeight,
            minHeight: minHeight,
            width: width,
            maxWidth: maxWidth,
            margin: '0',            
            padding: padding,
            paddingLeft: paddingLeft,
            paddingRight: paddingRight,
            paddingTop: paddingTop,
            paddingBottom: paddingBottom,
            fontFamily: designSystemStyles.fontFamily,
            backgroundColor: backgroundColor,
            alignItems: alignment,
            justifyContent: justifyContent,
            borderRadius: designSystemStyles.cornerRadiusComponent
        }}>
            {children}
        </div>
    );
};

export const TableHeaderCell: React.FC<{text: string, width?: string, justifyContent?: CSSProperties["justifyContent"], truncate?: boolean, title?: string}> = ({ text, width = 'auto', justifyContent = "left", truncate = false, title }) => {
    const resolvedTitle = title ?? (truncate ? text.replace(/\*\*/g, "").replace(/__/g, "").replace(/`/g, "") : undefined)
    return (
        <HorizontalContainer width={width} justifyContent={justifyContent} minWidth="0" overflow={truncate ? "hidden" : undefined} title={resolvedTitle}>
            <Text text={"**"+(text?.trim() ?? "")+"**"} markdown={true} truncate={truncate} title={resolvedTitle}/>
        </HorizontalContainer>
    )
}

export const TableCell: React.FC<{text: string, width?: string, justifyContent?: CSSProperties["justifyContent"], truncate?: boolean, title?: string, small?: boolean}> = ({ text, width = 'auto', justifyContent = "left", truncate = false, title, small = false }) => {
    const resolvedTitle = title ?? (truncate ? text.replace(/\*\*/g, "").replace(/__/g, "").replace(/`/g, "") : undefined)
    const CellText = small ? SmallText : Text
    return (
        <HorizontalContainer width={width} justifyContent={justifyContent} minWidth="0" overflow={truncate ? "hidden" : undefined} title={resolvedTitle}>
            <CellText text={text?.trim() ?? ""} markdown={true} truncate={truncate} title={resolvedTitle}/>
        </HorizontalContainer>
    )
}
